/**
 * Birthday decorations at home — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const birthdayDecorationsAtHomePack: ToilePack = {
    slug: 'birthday-decorations-at-home',
    category: 'occasions',
    name: 'Birthday decorations at home',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'wall', label: 'Birthday wall', subject: 'a wall with a "HAPPY BIRTHDAY" banner, balloons and fairy lights'},
        {key: 'table', label: 'Cake table', subject: 'a cake table against a decorated wall panel with balloons'},
        {key: 'room', label: 'Room surprise', subject: 'a bedroom with balloons on the floor and string lights'},
    ],
    styles: [
        {key: 'simple', label: 'Simple', style: 'a simple low-budget setup of a handful of balloons, paper fans and tape, seen straight on at eye level', palette: 'a few cheerful colours on a plain painted wall', framing: 'the setup centered with the room edges visible'},
        {key: 'rosegold', label: 'Rose gold', style: 'an elegant rose gold and white theme with foil balloons and a sequin curtain, a three-quarter view', palette: 'rose gold, blush pink and white', framing: 'the setup filling most of the frame'},
        {key: 'kids', label: 'Kids theme', style: "a bright kids' theme with paper stars, bunting and colourful balloons, a slightly low view at a child's height", palette: 'primary red, yellow and blue', framing: 'the setup centered, the floor in front'},
    ],
    directions: ['for husband', 'first birthday', 'jungle theme'],
    directionHint: 'try: for husband',
    seo: {
        title: 'Birthday decoration at home: wall, cake table, room',
        metaDescription: 'Nine simple birthday decoration pictures for a home: a birthday wall, a cake table and a room with string lights, in three themes.',
        h1: 'Birthday decoration at home: wall, cake table, room',
        tagline: "Nine pictures, three birthday spots in the home, each in a simple, a rose gold and a kids' theme.",
        intro: "Birthday decoration for a home party: a wall with a HAPPY BIRTHDAY banner, balloons and fairy lights; a cake table against a decorated wall panel; and a bedroom with balloons on the floor. The themes run from a simple setup of a few balloons and paper fans, to rose gold with foil balloons, to a bright kids' theme with paper stars. Type a twist, such as a first birthday or a jungle theme, and the same spots carry it.",
        useCases: [
            "a birthday wall for a child's party at home",
            'a cake table for a family birthday',
            'a bedroom surprise for a teenager',
            'a low-budget setup of balloons and paper fans',
        ],
        faq: [
            {q: 'What is the simple theme?', a: 'A few balloons, paper fans and tape on a plain painted wall, seen straight on. It is the low-budget setup of the three.'},
            {q: 'How is the rose gold theme different?', a: 'It uses rose gold, blush pink and white, with foil balloons and a sequin curtain, shown from a three-quarter view.'},
            {q: 'Can I ask for a theme for a child?', a: 'Yes. Type a twist such as first birthday or jungle theme, and the same three spots are shown in that theme.'},
            {q: 'Does any picture have writing on it?', a: 'Only the wall, which shows a HAPPY BIRTHDAY banner. The other pictures have no lettering.'},
        ],
    },
}
